import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { config } from '../config';
import { PATH_SEGMENTS, route } from '../config/paths';
import {
  createPublicDemoSession,
  demoTokenStorageKey,
  getPublicDemo,
  publicDemoCloseSession,
  publicDemoProcessTurn,
  publicDemoStartAudio,
  DemoCatalogEntry,
} from '../services/publicDemosClient';
import { buildMediaWebSocketUrl, LiveAssistMediaClient, unlockAudioContext } from '../utils/liveAssistMediaWs';
import '../styles/screens/LiveDemo.css';

type VoiceLine = { turn: number | null; role: 'agent' | 'customer'; text: string };

const CallBarButton: React.FC<{
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  danger?: boolean;
  halo?: boolean;
}> = ({ icon, label, onClick, disabled, pressed, danger, halo }) => (
  <span className={`live-demo-call-btn-wrap ${halo ? 'is-halo' : ''}`}>
    {halo ? (
      <span className="live-demo-call-halo" aria-hidden="true">
        <span />
        <span />
      </span>
    ) : null}
    <button
      type="button"
      className={`live-demo-call-btn ${pressed ? 'is-pressed' : ''} ${danger ? 'is-danger' : ''} ${halo ? 'is-live-mic' : ''}`}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="material-symbols-outlined" aria-hidden="true">
        {icon}
      </span>
    </button>
  </span>
);

const LiveDemoPlayer: React.FC = () => {
  const { demoId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const stateSessionId = (location.state as { sessionId?: string } | null)?.sessionId;

  const [sessionId, setSessionId] = useState<string | null>(stateSessionId || null);
  const [token, setToken] = useState<string | null>(null);
  const [lines, setLines] = useState<VoiceLine[]>([]);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [live, setLive] = useState(false);
  const [manualText, setManualText] = useState('');
  const [demoMeta, setDemoMeta] = useState<DemoCatalogEntry | null>(null);
  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  const mediaClientRef = useRef<LiveAssistMediaClient | null>(null);
  const micMutedRef = useRef(false);
  const speakerMutedRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const bootstrappedRef = useRef(false);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);
  const callRootRef = useRef<HTMLDivElement | null>(null);
  const composerInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  useEffect(() => {
    micMutedRef.current = micMuted;
    mediaClientRef.current?.setMicMuted(micMuted);
  }, [micMuted]);
  useEffect(() => {
    speakerMutedRef.current = speakerMuted;
    mediaClientRef.current?.setSpeakerMuted(speakerMuted);
  }, [speakerMuted]);

  useEffect(() => {
    const root = callRootRef.current;
    const vv = window.visualViewport;
    if (!root || !vv) return;
    const sync = () => {
      root.style.height = `${Math.round(vv.height)}px`;
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      root.style.height = '';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = await getPublicDemo(demoId);
        if (!cancelled) setDemoMeta(row);
      } catch {
        /* optional chrome */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [demoId]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [lines, interim, composerOpen]);

  useEffect(() => {
    if (!composerOpen) return;
    composerInputRef.current?.focus();
  }, [composerOpen]);

  const appendLine = useCallback((line: VoiceLine) => {
    setLines((prev) => [...prev, line]);
  }, []);

  const stopMedia = useCallback(() => {
    mediaClientRef.current?.stop();
    mediaClientRef.current = null;
    setLive(false);
  }, []);

  const startMedia = useCallback(
    async (sid: string, demoToken: string, mediaPath: string) => {
      stopMedia();
      let captureContext: AudioContext;
      let micStream: MediaStream;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        captureContext = new AudioContext();
        await unlockAudioContext(captureContext);
        const silent = new Audio(
          'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAA='
        );
        silent.volume = 0.01;
        await silent.play().catch(() => undefined);
      } catch {
        setError('Microphone permission is required for the live demo');
        return;
      }
      const wsUrl = buildMediaWebSocketUrl(config.api.baseUrl, mediaPath, demoToken);
      const client = new LiveAssistMediaClient({
        onReady: () => {
          client.setSpeaker('customer');
          setError(null);
          setLive(true);
          setBusy(false);
        },
        onPartialStt: (text) => setInterim(text),
        onSttFinal: (text) => {
          setInterim('');
          if (text.trim()) appendLine({ turn: null, role: 'customer', text: text.trim() });
        },
        onBotSpeech: (turn, text) => {
          if (text.trim()) appendLine({ turn, role: 'agent', text: text.trim() });
        },
        onError: (message) => setError(message),
        onClose: () => setLive(false),
      });
      client.attachSessionAudio(captureContext, micStream);
      client.setMicMuted(micMutedRef.current);
      client.setSpeakerMuted(speakerMutedRef.current);
      mediaClientRef.current = client;
      try {
        await client.connect(wsUrl);
      } catch (e) {
        stopMedia();
        micStream.getTracks().forEach((t) => t.stop());
        captureContext.close().catch(() => undefined);
        setError(e instanceof Error ? e.message : 'Failed to start media stream');
      }
    },
    [appendLine, stopMedia]
  );

  const bootstrapSession = useCallback(
    async (sid: string, demoToken: string) => {
      const bootstrapRaw = sessionStorage.getItem(`demo_bootstrap:${sid}`);
      if (bootstrapRaw) {
        try {
          const boot = JSON.parse(bootstrapRaw) as {
            result?: { voice_bot?: { speech?: string } };
          };
          const speech = boot?.result?.voice_bot?.speech;
          if (typeof speech === 'string' && speech.trim()) {
            appendLine({ turn: 0, role: 'agent', text: speech.trim() });
          }
        } catch {
          /* ignore */
        }
        sessionStorage.removeItem(`demo_bootstrap:${sid}`);
      }
      const audio = await publicDemoStartAudio(sid, demoToken);
      const mediaPath =
        (typeof audio.media_ws_path === 'string' && audio.media_ws_path) ||
        `/live-assist/sessions/${sid}/media`;
      await startMedia(sid, demoToken, mediaPath);
    },
    [appendLine, startMedia]
  );

  useEffect(() => {
    let cancelled = false;
    if (bootstrappedRef.current) {
      return () => {
        cancelled = true;
      };
    }
    bootstrappedRef.current = true;
    (async () => {
      setBusy(true);
      setError(null);
      try {
        let sid = stateSessionId || null;
        let demoToken: string | null = sid ? sessionStorage.getItem(demoTokenStorageKey(sid)) : null;

        if (!sid || !demoToken) {
          const data = await createPublicDemoSession(demoId);
          sid = data.session.session_id;
          demoToken = data.access_token;
          sessionStorage.setItem(demoTokenStorageKey(sid), demoToken);
          sessionStorage.setItem(
            `demo_bootstrap:${sid}`,
            JSON.stringify({ turn: data.turn, result: data.result, demo_id: data.demo_id })
          );
        }
        if (cancelled || !sid || !demoToken) return;
        setSessionId(sid);
        setToken(demoToken);
        await bootstrapSession(sid, demoToken);
      } catch (e) {
        if (!cancelled) {
          bootstrappedRef.current = false;
          setError(e instanceof Error ? e.message : 'Failed to start demo session');
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoId]);

  useEffect(() => {
    return () => {
      stopMedia();
    };
  }, [stopMedia]);

  const handleClose = async () => {
    stopMedia();
    const sid = sessionIdRef.current;
    const demoToken = tokenRef.current;
    if (sid && demoToken) {
      try {
        await publicDemoCloseSession(sid, demoToken);
      } catch {
        /* ignore */
      }
      sessionStorage.removeItem(demoTokenStorageKey(sid));
    }
    navigate(route(PATH_SEGMENTS.LIVE_DEMO, demoId));
  };

  const handleManual = async () => {
    if (!sessionId || !token || sending || !manualText.trim()) return;
    const text = manualText.trim();
    setSending(true);
    try {
      appendLine({ turn: null, role: 'customer', text });
      setManualText('');
      const data = await publicDemoProcessTurn(sessionId, token, text);
      // live === media_ready; when live, agent text comes from onBotSpeech with TTS.
      if (!live) {
        const speech = (data.result?.voice_bot as { speech?: string } | undefined)?.speech;
        if (typeof speech === 'string' && speech.trim()) {
          appendLine({ turn: data.turn, role: 'agent', text: speech.trim() });
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Turn failed');
    } finally {
      setSending(false);
    }
  };

  const agentName = demoMeta?.agent?.display_name || 'Agent';
  const callType = demoMeta?.title || 'Voice call';
  const statusLabel = live
    ? micMuted
      ? 'Mic muted'
      : speakerMuted
        ? 'Speaker muted'
        : 'Listening'
    : busy
      ? 'Connecting…'
      : 'Idle';

  const canSend = Boolean(sessionId && token && manualText.trim() && !sending);

  return (
    <div ref={callRootRef} className={`live-demo-call ${composerOpen ? 'is-composing' : ''}`}>
      <div className="live-demo-call-atmosphere" aria-hidden="true" />
      <div className="live-demo-call-frame live-demo-call-main">
        <section
          className={`live-demo-call-panel ${live ? 'is-live' : ''} ${micMuted ? 'is-muted' : ''}`}
        >
          <div className="live-demo-call-panel-head">
            <Link className="live-demo-call-logo" to={route()} aria-label="Vhearu home">
              <img src="/vhu-mmt-speechbubble.png" alt="" />
            </Link>
            <div className="live-demo-call-identity">
              <div className="live-demo-call-agent">{agentName}</div>
              <div className="live-demo-call-type">{callType}</div>
            </div>
            <div className={`live-demo-call-live ${live ? 'is-live' : ''}`}>
              <span className="live-demo-call-live-dot" />
              {statusLabel}
            </div>
          </div>

          <div className="live-demo-call-script">
            {error && <div className="live-demo-error">{error}</div>}
            {lines.length === 0 && !interim && (
              <div className="live-demo-transcript-empty">
                {busy
                  ? 'Connecting to the agent…'
                  : 'The agent will greet you shortly. Speak, or open chat to type.'}
              </div>
            )}
            {lines.map((line, idx) => (
              <div
                key={`${line.role}-${idx}`}
                className={`live-demo-bubble live-demo-bubble-${line.role}`}
              >
                <span className="live-demo-bubble-role">
                  {line.role === 'agent' ? agentName : 'You'}
                </span>
                <p className="live-demo-bubble-text">{line.text}</p>
              </div>
            ))}
            {interim ? (
              <div className="live-demo-bubble live-demo-bubble-customer live-demo-interim">
                <span className="live-demo-bubble-role">You</span>
                <p className="live-demo-bubble-text">{interim}</p>
              </div>
            ) : null}
            <div ref={transcriptEndRef} />
          </div>

          {composerOpen && (
            <form
              className="live-demo-call-composer"
              onSubmit={(e) => {
                e.preventDefault();
                handleManual();
              }}
            >
              <input
                ref={composerInputRef}
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                placeholder="Type a reply…"
                aria-label="Type a reply"
              />
              <button
                type="submit"
                className="live-demo-call-send"
                disabled={!canSend}
                aria-label="Send"
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  send
                </span>
              </button>
            </form>
          )}

          <div className="live-demo-call-bar">
            <CallBarButton
              icon={micMuted ? 'mic_off' : 'mic'}
              label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
              pressed={micMuted}
              disabled={!live}
              halo={live && !micMuted}
              onClick={() => setMicMuted((muted) => !muted)}
            />
            <CallBarButton
              icon={speakerMuted ? 'volume_off' : 'volume_up'}
              label={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
              pressed={speakerMuted}
              disabled={!live}
              onClick={() => setSpeakerMuted((muted) => !muted)}
            />
            <CallBarButton
              icon="chat_bubble"
              label={composerOpen ? 'Hide text reply' : 'Type a reply'}
              pressed={composerOpen}
              onClick={() => setComposerOpen((open) => !open)}
            />
            <CallBarButton icon="call_end" label="End call" danger onClick={handleClose} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default LiveDemoPlayer;
